import { useState, useEffect, useRef, useCallback } from "react";
import { Footer, Composer, AISparkle, NewChatButton, UserAvatar, chatSvg, PrimaryButton, SecondaryButton } from "./shared";
import { FeedbackModal, FeedbackToast, CheckIcon, ConfirmDialog, CheckGlyph, CloseGlyph } from "./feedbackUi";
// import { SuggestedPrompts } from "./suggestedPrompts";
import { SourceDocViewer } from "./sourceDocViewer";
import { streamChatbot, buildHistory, saveChatHistory, sourceLabel, sourceFile, logChatQuery, submitChatFeedback } from "./chatbotService";
import { MarkdownMessage } from "./markdownMessage";

const SUGGESTED_PROMPTS = [
  "Summarise the latest DBS dataset",
  "What are the key risk trends?",
  "Show approved document extracts",
];

const GENERIC_ERROR = { title: "Something went wrong", body: "We couldn't generate a response for this request. Please check your connection and try again." };

function formatDayTime() {
  const now = new Date();
  const day = now.toLocaleDateString("en-US", { weekday: "long" });
  const time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
  return `${day} ${time}`;
}

let idCounter = 0;
const randomId = (prefix) => {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return `${prefix}_${c.randomUUID()}`;
  // randomUUID is only exposed in secure contexts; getRandomValues is available everywhere.
  if (c && typeof c.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${prefix}_${hex}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${++idCounter}`;
};
const nextTurnId = () => randomId("turn");
// Local-only correlation id used to attach an async chat/log query_id back onto
// the right variant once it resolves, without relying on array index (which
// can shift if the user paginates or regenerates while the request is in flight).
const nextLogId = () => randomId("log");

// ─── Source chip (first AI response only) ─────────────────────────────────────

function SourceChip({ label, file, onOpen }) {
  const [hovered, setHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const clickable = Boolean(file && onOpen);

  const handleOpen = () => {
    if (!clickable || loading) return;
    setLoading(true);
    Promise.resolve(onOpen(file)).catch(() => {}).finally(() => setLoading(false));
  };

  const baseStyle = {
    display: "inline-flex", alignItems: "center", gap: "var(--spacing-4, 4px)", height: 22,
    padding: "0 var(--spacing-8, 8px)", borderRadius: "var(--border-radius-circle, 200px)",
    border: "1px solid var(--border-colors-default-secondary, #afcdfb)",
    backgroundColor: clickable && hovered ? "var(--bg-colors-hover-secondary, #d8e5fd)" : "var(--bg-colors-default-primary, #ffffff)",
    flexShrink: 0,
    cursor: clickable ? "pointer" : "default",
    opacity: loading ? 0.6 : 1,
    transition: "background-color 0.15s ease",
  };

  const content = (
    <>
      <svg style={{ width: 10, height: 10, flexShrink: 0 }} fill="none" viewBox="0 0 12 14" preserveAspectRatio="xMidYMid meet">
        <path clipRule="evenodd" d="M1.5 0C0.671573 0 0 0.671573 0 1.5V12.5C0 13.3284 0.671573 14 1.5 14H10.5C11.3284 14 12 13.3284 12 12.5V4.5L7.5 0H1.5ZM7 1.25V4C7 4.55228 7.44772 5 8 5H10.75L7 1.25ZM2.5 7C2.5 6.72386 2.72386 6.5 3 6.5H9C9.27614 6.5 9.5 6.72386 9.5 7C9.5 7.27614 9.27614 7.5 9 7.5H3C2.72386 7.5 2.5 7.27614 2.5 7ZM3 9.5C2.72386 9.5 2.5 9.72386 2.5 10C2.5 10.2761 2.72386 10.5 3 10.5H7C7.27614 10.5 7.5 10.2761 7.5 10C7.5 9.72386 7.27614 9.5 7 9.5H3Z" fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
      </svg>
      <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.75rem)", lineHeight: "18px", color: "var(--text-colors-default-primary, #2e2e38)", whiteSpace: "nowrap", textDecoration: clickable && hovered ? "underline" : "none" }}>{label}</span>
    </>
  );

  if (!clickable) {
    return <span style={baseStyle}>{content}</span>;
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`Open source document: ${label}`}
      title={`Open ${file}`}
      style={{ ...baseStyle, font: "inherit" }}
    >
      {content}
    </button>
  );
}

// ─── Shared tooltip bubble ─────────────────────────────────────────────────────

function ChipTooltip({ label }) {
  return (
    <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", pointerEvents: "none", filter: "drop-shadow(0px 2px 2px rgba(21,92,180,0.06)) drop-shadow(0px 8px 8px rgba(21,92,180,0.08))", zIndex: 20 }}>
      <div style={{ backgroundColor: "#2e2e38", borderRadius: "var(--border-radius-sm, 4px)", padding: "var(--spacing-8, 8px) var(--spacing-12, 12px)" }}>
        <p style={{ fontSize: "var(--font-size-12, 0.6875rem)", lineHeight: "18px", color: "var(--eyp-theme-neutral-25-off-white, #f6f6fa)", margin: 0, textAlign: "center", whiteSpace: "nowrap" }}>{label}</p>
      </div>
      <div style={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "6px solid #2e2e38" }} />
    </div>
  );
}

// ─── User message ──────────────────────────────────────────────────────────────

function EditChip({ onClick }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {hovered && <ChipTooltip label="Edit" />}
      <button
        type="button"
        onClick={onClick}
        aria-label="Edit message"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setPressed(false); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{ width: 24, height: 24, borderRadius: 16, backgroundColor: pressed ? "#afcdfb" : hovered ? "#d8e5fd" : "var(--bg-colors-default-primary, #ffffff)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "none", padding: 0, transition: "background-color 0.15s ease" }}
      >
        <div style={{ position: "relative", width: 16, height: 16 }}>
          <svg style={{ position: "absolute", inset: "12%", width: "76%", height: "76%" }} fill="none" viewBox="0 0 12.1655 12.1655" preserveAspectRatio="none">
            <path clipRule="evenodd" d={chatSvg.p38956a00} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
          </svg>
        </div>
      </button>
    </div>
  );
}

function UserMessage({ turn, onEdit }) {
  return (
    <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "flex-start", justifyContent: "flex-end", width: "100%" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", maxWidth: "70%" }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28 }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>You</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{turn.time}</span>
        </div>
        <div style={{ backgroundColor: "var(--bg-colors-default-primary, #ffffff)", borderRadius: "4px 0 4px 4px", padding: "10px 14px" }}>
          <p style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", color: "var(--eyp-theme-neutral-900, #23232f)", margin: 0, lineHeight: "1.5", whiteSpace: "pre-wrap" }}>{turn.userText}</p>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <EditChip onClick={onEdit} />
        </div>
      </div>
      <UserAvatar size={32} />
    </div>
  );
}

function UserMessageEditor({ turn, onSave, onCancel }) {
  const [text, setText] = useState(turn.userText);
  const ref = useRef(null);

  useEffect(() => { ref.current && ref.current.focus(); }, []);

  const canSave = !!text.trim();

  return (
    <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "flex-start", justifyContent: "flex-end", width: "100%" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end", width: "min(420px, 80%)" }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28, width: "100%" }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>You</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{turn.time}</span>
        </div>
        <div style={{ width: "100%", backgroundColor: "var(--bg-colors-default-primary, #ffffff)", border: "2px solid var(--accent-info-primary, #0f69ae)", borderRadius: "var(--border-radius-sm, 4px)", padding: "10px 14px", display: "flex", flexDirection: "column", gap: "var(--spacing-8, 8px)" }}>
          <textarea
            ref={ref}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            aria-label="Edit message text"
            style={{ width: "100%", border: "none", outline: "none", resize: "none", background: "transparent", fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "1.5", color: "var(--eyp-theme-neutral-900, #23232f)", boxSizing: "border-box" }}
          />
          <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", justifyContent: "flex-end" }}>
            <SecondaryButton onClick={onCancel} ariaLabel="Cancel edit" style={{ width: 28, height: 28, padding: 6 }}><CloseGlyph size={16} /></SecondaryButton>
            <PrimaryButton onClick={() => canSave && onSave(text.trim())} disabled={!canSave} ariaLabel="Save edit" style={{ width: 28, height: 28, padding: 6 }}><CheckGlyph size={12} /></PrimaryButton>
          </div>
        </div>
      </div>
      <UserAvatar size={32} />
    </div>
  );
}

// ─── Loading indicator ─────────────────────────────────────────────────────────

function LoadingDots() {
  return (
    <div style={{ display: "inline-flex", gap: 5, alignItems: "center", backgroundColor: "#f4f7fc", padding: "12px 9px", borderRadius: "0 4px 4px 4px" }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "var(--eyp-theme-neutral-600-gray-01, #747480)", animation: `dbs-bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

function LoadingMessage({ time }) {
  return (
    <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "flex-start", alignSelf: "flex-start", width: "70%", minWidth: 0, textAlign: "left" }}>
      <AISparkle size={32} />
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28 }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>DBS AI Chatbot</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{time}</span>
        </div>
        <LoadingDots />
      </div>
    </div>
  );
}

// ─── AI response ───────────────────────────────────────────────────────────────

function ActionChip({ children, label, onClick, selected = false }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const bg = pressed ? "#afcdfb" : selected ? "#d8e5fd" : hovered ? "#d8e5fd" : "var(--bg-colors-default-extra-a, #f4f7fc)";
  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
      {hovered && <ChipTooltip label={label} />}
      <div
        onClick={onClick}
        role="button"
        aria-label={label}
        aria-pressed={selected}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { setHovered(false); setPressed(false); }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        style={{ width: 24, height: 24, borderRadius: 16, backgroundColor: bg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "background-color 0.15s ease" }}
      >
        {children}
      </div>
    </div>
  );
}

function ResponsePagination({ index, total, onPrev, onNext }) {
  const arrowBtn = (dir, disabled, onClick) => (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={dir === "prev" ? "Previous response" : "Next response"}
      style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", cursor: disabled ? "default" : "pointer", padding: 0, opacity: disabled ? 0.35 : 1 }}
    >
      <svg style={{ width: 12, height: 12, transform: dir === "prev" ? "rotate(90deg)" : "rotate(-90deg)" }} fill="none" viewBox="0 0 16 16" preserveAspectRatio="none">
        <path d="M4 6L8 10L12 6" stroke="var(--icon-colors-default-primary, #064372)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-4, 4px)", marginLeft: "var(--spacing-8, 8px)" }}>
      {arrowBtn("prev", index === 0, onPrev)}
      <span style={{ fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", whiteSpace: "nowrap", lineHeight: "18px" }}>{index + 1}/{total}</span>
      {arrowBtn("next", index === total - 1, onNext)}
    </div>
  );
}

function RegenerateButton({ onClick }) {
  return (
    <SecondaryButton onClick={onClick} style={{ height: 32, padding: "6px 14px", alignSelf: "flex-start" }}>
      <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}>
        <svg style={{ position: "absolute", inset: "5%", width: "90%", height: "90%" }} fill="none" viewBox="0 0 17.8756 17.9167" preserveAspectRatio="none">
          <path clipRule="evenodd" d={chatSvg.p30d66080} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
          <path clipRule="evenodd" d={chatSvg.p1539e400} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
          <path clipRule="evenodd" d={chatSvg.p2b630880} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
          <path clipRule="evenodd" d={chatSvg.p2edd7480} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
        </svg>
      </div>
      <span style={{ whiteSpace: "nowrap" }}>Regenerate</span>
    </SecondaryButton>
  );
}

// Live view while the answer streams in token-by-token.
function StreamingMessage({ turn }) {
  return (
    <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "flex-start", width: "70%", maxWidth: "70%", minWidth: 0, alignSelf: "flex-start", textAlign: "left" }}>
      <AISparkle size={32} />
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28 }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>DBS AI Chatbot</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{turn.time}</span>
        </div>
        <div style={{ backgroundColor: "#f4f7fc", borderRadius: "0 4px 4px 4px", padding: "20px 14px", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", minWidth: 0, width: "100%", textAlign: "left" }}>
          <MarkdownMessage text={turn.streamText || ""} />
          <span className="dbs-caret" aria-hidden="true" style={{ display: "inline-block", width: 2, height: "1em", backgroundColor: "var(--eyp-color-blue-900-dark-blue, #072e65)" }} />
        </div>
      </div>
    </div>
  );
}

function AIResponseMessage({ turn, onFeedback, onRegenerate, onPrev, onNext, onViewSource }) {
  const [copied, setCopied] = useState(false);
  const content = turn.variants[turn.activeIndex];
  const total = turn.variants.length;
  const sentiment = content?.feedback?.sentiment;
  const likeColor = sentiment === "like" ? "#000000" : "var(--icon-colors-default-primary, #064372)";
  const dislikeColor = sentiment === "dislike" ? "#000000" : "var(--icon-colors-default-primary, #064372)";

  const bodyStyle = { fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", color: "var(--eyp-theme-neutral-1000-confident-black, #1a1a24)", margin: 0, lineHeight: "1.5" };

  const handleCopy = () => {
    const themesText = content.keyThemes ? "\n\nKey themes observed:\n" + content.keyThemes.map((t) => `• ${t.title} ${t.body}`).join("\n") : "";
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(content.paragraphs.join("\n\n") + themesText).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "flex-start", width: "70%", maxWidth: "70%", minWidth: 0, alignSelf: "flex-start", textAlign: "left" }}>
      <AISparkle size={32} />
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28 }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>DBS AI Chatbot</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{turn.time}</span>
        </div>
        <div style={{ backgroundColor: "#f4f7fc", borderRadius: "0 4px 4px 4px", padding: "20px 14px", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", minWidth: 0, width: "100%", textAlign: "left" }}>
          <MarkdownMessage text={content.paragraphs.join("\n\n")} />
          {content.keyThemes && (
            <>
              <div style={{ height: 1, backgroundColor: "var(--border-colors-default-secondary, #afcdfb)", borderRadius: 2 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-8, 8px)" }}>
                <p style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0, lineHeight: "20px" }}>Key themes observed:</p>
                <ul style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 4 }}>
                  {content.keyThemes.map((t, i) => (
                    <li key={i} style={{ ...bodyStyle }}>
                      <strong style={{ fontWeight: 400 }}>{t.title}</strong>
                      {" " + t.body}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
          {content.sources?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-6, 6px)", alignItems: "center" }}>
              {content.sources.map((src, i) => {
                const label = sourceLabel(src);
                return (
                  <SourceChip key={`${label}-${i}`} label={label} file={sourceFile(src)} onOpen={onViewSource} />
                );
              })}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "var(--spacing-4, 4px)", alignItems: "center" }}>
          <ActionChip label={copied ? "Copied" : "Copy"} onClick={handleCopy}>
            {copied ? (
              <CheckIcon />
            ) : (
              <div style={{ position: "relative", width: 16, height: 16 }}>
                <svg style={{ position: "absolute", inset: "13%", width: "74%", height: "74%" }} fill="none" viewBox="0 0 11.6667 11.6667" preserveAspectRatio="none">
                  <path clipRule="evenodd" d={chatSvg.p833aa80} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
                  <path clipRule="evenodd" d={chatSvg.p15f29ef0} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
                </svg>
              </div>
            )}
          </ActionChip>
          <ActionChip label="Like" onClick={() => onFeedback("like")} selected={sentiment === "like"}>
            <div style={{ position: "relative", width: 16, height: 16 }}>
              <svg style={{ position: "absolute", inset: "8% 12% 13% 11%", width: "77%", height: "79%" }} fill="none" viewBox="0 0 12.2554 12.5203" preserveAspectRatio="none">
                <path clipRule="evenodd" d={chatSvg.p3d42b980} fill={likeColor} fillRule="evenodd" />
                <path clipRule="evenodd" d={chatSvg.p107dcb80} fill={likeColor} fillRule="evenodd" />
              </svg>
            </div>
          </ActionChip>
          <ActionChip label="Dislike" onClick={() => onFeedback("dislike")} selected={sentiment === "dislike"}>
            <div style={{ position: "relative", width: 16, height: 16 }}>
              <svg style={{ position: "absolute", inset: "11% 12% 10% 11%", width: "77%", height: "79%" }} fill="none" viewBox="0 0 12.2554 12.5203" preserveAspectRatio="none">
                <path clipRule="evenodd" d={chatSvg.p2a003300} fill={dislikeColor} fillRule="evenodd" />
                <path clipRule="evenodd" d={chatSvg.p38748280} fill={dislikeColor} fillRule="evenodd" />
              </svg>
            </div>
          </ActionChip>
          {total > 1 && (
            <ResponsePagination index={turn.activeIndex} total={total} onPrev={onPrev} onNext={onNext} />
          )}
        </div>
        <RegenerateButton onClick={onRegenerate} />
      </div>
    </div>
  );
}

// ─── Error response ────────────────────────────────────────────────────────────

function ErrorMessage({ turn, onRetry }) {
  return (
    <div style={{ display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "flex-start", width: "70%", maxWidth: "70%", minWidth: 0, alignSelf: "flex-start", textAlign: "left" }}>
      <AISparkle size={32} />
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", minWidth: 0 }}>
        <div style={{ display: "flex", gap: "var(--spacing-8, 8px)", alignItems: "center", height: 28 }}>
          <span style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "var(--text-colors-default-primary, #2e2e38)", lineHeight: "20px" }}>DBS AI Chatbot</span>
          <span style={{ fontWeight: 300, fontSize: "var(--font-size-12, 0.6875rem)", color: "var(--text-colors-default-secondary, #656579)", lineHeight: "18px" }}>{turn.time}</span>
        </div>
        <div style={{ backgroundColor: "#fef1f1", border: "1px solid #ea011b", borderRadius: "0 4px 4px 4px", padding: "14px", display: "flex", gap: "var(--spacing-12, 12px)", alignItems: "flex-start" }}>
          <div style={{ position: "relative", width: 20, height: 20, flexShrink: 0, marginTop: 1 }}>
            <svg style={{ width: 20, height: 20 }} fill="none" viewBox="0 0 20 20" preserveAspectRatio="none">
              <circle cx="10" cy="10" r="9" stroke="#ea011b" strokeWidth="1.5" />
              <path d="M10 5.5V10.5" stroke="#ea011b" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="14" r="0.9" fill="#ea011b" />
            </svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4, 4px)", minWidth: 0 }}>
            <p style={{ fontSize: "var(--font-size-14, 0.875rem)", color: "#b8000f", margin: 0, lineHeight: "20px" }}>{(turn.errorInfo && turn.errorInfo.title) || "Something went wrong"}</p>
            <p style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", color: "var(--eyp-theme-neutral-1000-confident-black, #1a1a24)", margin: 0, lineHeight: "1.5" }}>{(turn.errorInfo && turn.errorInfo.body) || "We couldn't generate a response for this request. Please check your connection and try again."}</p>
          </div>
        </div>
        <SecondaryButton onClick={onRetry} style={{ height: 32, padding: "6px 14px", alignSelf: "flex-start" }}>
          <div style={{ position: "relative", width: 18, height: 18, flexShrink: 0 }}>
            <svg style={{ position: "absolute", inset: "5%", width: "90%", height: "90%" }} fill="none" viewBox="0 0 17.8756 17.9167" preserveAspectRatio="none">
              <path clipRule="evenodd" d={chatSvg.p30d66080} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
              <path clipRule="evenodd" d={chatSvg.p1539e400} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
              <path clipRule="evenodd" d={chatSvg.p2b630880} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
              <path clipRule="evenodd" d={chatSvg.p2edd7480} fill="var(--icon-colors-default-primary, #064372)" fillRule="evenodd" />
            </svg>
          </div>
          <span style={{ whiteSpace: "nowrap" }}>Try again</span>
        </SecondaryButton>
      </div>
    </div>
  );
}

// ─── Chat View ─────────────────────────────────────────────────────────────────

export function ChatView({ initialText, onNewChat, sessionId, initialTurns, onSaved }) {
  // When restoring a previous session we hydrate from initialTurns and skip the
  // initial auto-query; otherwise we seed a single loading turn for initialText.
  const restored = Array.isArray(initialTurns) && initialTurns.length > 0;
  const [turns, setTurns] = useState(() =>
    restored
      ? initialTurns.map((t) => ({ ...t, loading: false, streaming: false, streamText: "", error: false }))
      : [{ id: nextTurnId(), userText: initialText, time: formatDayTime(), variants: [], activeIndex: 0, loading: true }]
  );
  const [composer, setComposer] = useState("");
  const [mode, setMode] = useState("Deep Research");
  const [busy, setBusy] = useState(!restored);
  const [editingId, setEditingId] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [docViewer, setDocViewer] = useState(null);
  const scrollRef = useRef(null);
  const mountedRef = useRef(true);
  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const abortRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const savedOnceRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Best-effort snapshot save of the current session (never blocks the chat).
  const persist = useCallback((turnsSnapshot) => {
    if (!sessionId) return;
    const snapshot = turnsSnapshot ?? turnsRef.current;
    saveChatHistory({ sessionId, turns: snapshot, mode: modeRef.current }).then((ok) => {
      // Refresh the sidebar list after the first successful save of a session.
      if (ok && !savedOnceRef.current && onSaved) {
        savedOnceRef.current = true;
        onSaved();
      }
    });
  }, [sessionId, onSaved]);

  const finalizeAnswer = (turnId, answer) => {
    const next = turnsRef.current.map((t) => {
      if (t.id !== turnId) return t;
      if (t.regenerate) {
        const variants = [...t.variants, answer];
        return { ...t, variants, activeIndex: variants.length - 1, loading: false, streaming: false, streamText: "", error: false, errorInfo: undefined, regenerate: false };
      }
      return { ...t, variants: [answer], activeIndex: 0, loading: false, streaming: false, streamText: "", error: false, errorInfo: undefined };
    });
    setTurns(next);
    // Persist once the response is finalized (best-effort).
    persist(next);
  };

  // Fires chat/log for a completed (or partially-completed) turn and, once the
  // API returns a query_id, attaches it to the specific variant - matched by a
  // local-only id so pagination/regenerate racing can't attach it to the wrong
  // variant. Best-effort: logChatQuery never throws and a missing query_id is
  // silently ignored (feedback will just have no query_id to send yet).
  const logAndAttachQueryId = (turnId, logId, logArgs) => {
    logChatQuery(logArgs).then((queryId) => {
      if (!queryId || !mountedRef.current) return;
      setTurns((prev) => prev.map((t) => {
        if (t.id !== turnId) return t;
        return { ...t, variants: t.variants.map((v) => (v._logId === logId ? { ...v, queryId } : v)) };
      }));
    });
  };

  // Streams the answer from the API into the target turn, updating live as tokens
  // arrive. A turn flagged regenerate appends a new variant; otherwise it replaces.
  const runQuery = useCallback(async (turnId, query, historyTurns) => {
    const history = buildHistory(historyTurns);
    const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
    abortRef.current = controller;

    const onToken = (chunk) => {
      if (!mountedRef.current) return;
      setTurns((prev) => prev.map((t) => t.id === turnId
        ? { ...t, loading: false, streaming: true, streamText: (t.streamText || "") + chunk }
        : t));
    };

    try {
      const answer = await streamChatbot({ query, history, onToken, signal: controller?.signal });
      if (!mountedRef.current) return;
      const logId = nextLogId();
      finalizeAnswer(turnId, { ...answer, _logId: logId });
      logAndAttachQueryId(turnId, logId, { query, answer, succeeded: true, historySessionId: sessionId });
    } catch (error) {
      if (!mountedRef.current) return;
      if (error?.name === "AbortError") {
        // A superseded request (e.g. React StrictMode's double-invoke) was aborted
        // while a newer one is active: ignore it so we don't clobber the live turn.
        if (abortRef.current !== controller) return;
        // User-initiated stop: keep whatever streamed so far as the final answer.
        const partial = (turnsRef.current.find((t) => t.id === turnId)?.streamText || "").trim();
        if (partial) {
          const paragraphs = partial.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
          const logId = nextLogId();
          finalizeAnswer(turnId, { paragraphs, sources: [], _logId: logId });
          // A user-stopped stream still produced a real (if truncated) answer, so
          // it's logged as succeeded with no eval/response-time data available.
          logAndAttachQueryId(turnId, logId, { query, answer: { text: partial, responseTimeMs: null, eval: null }, succeeded: true, historySessionId: sessionId });
        } else {
          setTurns((prev) => prev.map((t) => t.id === turnId ? { ...t, loading: false, streaming: false, streamText: "", regenerate: false } : t));
        }
      } else {
        logChatQuery({ query, answer: null, succeeded: false, errorMessage: error?.message, historySessionId: sessionId });
        setTurns((prev) => prev.map((t) => t.id === turnId ? { ...t, loading: false, streaming: false, streamText: "", error: true, errorInfo: GENERIC_ERROR, regenerate: false } : t));
      }
    } finally {
      // Only clear shared state if this request is still the active one.
      if (abortRef.current === controller) {
        if (mountedRef.current) setBusy(false);
        abortRef.current = null;
      }
    }
  }, []);

  const handleStop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  useEffect(() => {
    // Restored sessions are already complete; don't re-fire the first query.
    if (restored) return;
    const first = turnsRef.current[0];
    if (first) runQuery(first.id, first.userText, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runQuery]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const handleSend = (override) => {
    const raw = typeof override === "string" ? override : composer;
    if (!raw.trim() || busy) return;
    const text = raw.trim();
    const priorTurns = turnsRef.current;
    const newTurn = { id: nextTurnId(), userText: text, time: formatDayTime(), variants: [], activeIndex: 0, loading: true };
    setComposer("");
    setBusy(true);
    setTurns((prev) => [...prev, newTurn]);
    persist([...priorTurns, newTurn]);
    runQuery(newTurn.id, text, priorTurns);
  };

  const handleRetry = (id) => {
    if (busy) return;
    const idx = turnsRef.current.findIndex((t) => t.id === id);
    if (idx === -1) return;
    const target = turnsRef.current[idx];
    const priorTurns = turnsRef.current.slice(0, idx);
    setBusy(true);
    setTurns((prev) => prev.map((t) => t.id === id ? { ...t, loading: true, error: false, errorInfo: undefined } : t));
    runQuery(id, target.userText, priorTurns);
  };

  const handleRegenerate = (id) => {
    if (busy) return;
    const target = turnsRef.current.find((t) => t.id === id);
    if (!target) return;
    const idx = turnsRef.current.indexOf(target);
    const priorTurns = turnsRef.current.slice(0, idx);
    setBusy(true);
    setTurns((prev) => prev.map((t) => t.id === id ? { ...t, loading: true, regenerate: true } : t));
    runQuery(id, target.userText, priorTurns);
  };

  const handlePaginate = (id, dir) => {
    setTurns((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      const next = Math.min(Math.max(t.activeIndex + dir, 0), t.variants.length - 1);
      return { ...t, activeIndex: next };
    }));
  };

  const applyEdit = (id, text) => {
    setEditingId(null);
    setPendingEdit(null);
    setBusy(true);
    let priorTurns = [];
    setTurns((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1) return prev;
      priorTurns = prev.slice(0, idx);
      const kept = prev.slice(0, idx + 1);
      kept[idx] = { ...kept[idx], userText: text, variants: [], activeIndex: 0, loading: true, error: false, errorInfo: undefined, regenerate: false };
      return kept;
    });
    runQuery(id, text, priorTurns);
  };

  const handleEditSave = (id, text) => {
    const idx = turns.findIndex((t) => t.id === id);
    if (idx !== -1 && idx < turns.length - 1) {
      setPendingEdit({ id, text });
      return;
    }
    applyEdit(id, text);
  };

  const openFeedback = (turnId, variantIndex, sentiment) => {
    setFeedbackTarget({ turnId, variantIndex, sentiment });
    setFeedbackOpen(true);
  };

  const handleFeedbackSubmit = (details = {}) => {
    const target = feedbackTarget;
    setFeedbackOpen(false);
    setToastOpen(true);
    if (!target) return;
    const feedback = {
      sentiment: target.sentiment,
      category: details.category ?? "",
      comment: details.comment ?? "",
      submittedOn: new Date().toISOString(),
    };
    const next = turnsRef.current.map((t) => {
      if (t.id !== target.turnId) return t;
      const variants = t.variants.map((v, i) => (i === target.variantIndex ? { ...v, feedback } : v));
      return { ...t, variants };
    });
    setTurns(next);
    setFeedbackTarget(null);
    persist(next);

    // Best-effort, not awaited: the toast above already confirms submission to
    // the user regardless of this network call's outcome. queryId may still be
    // null if chat/log's response hasn't come back yet for this variant.
    const targetVariant = next.find((t) => t.id === target.turnId)?.variants[target.variantIndex];
    submitChatFeedback({
      queryId: targetVariant?.queryId ?? null,
      historySessionId: sessionId,
      sentiment: target.sentiment,
      category: details.category,
      comment: details.comment,
    });
  };

  return (
    <div style={{ flex: "1 0 0", position: "relative", width: "100%", display: "flex", flexDirection: "column", padding: "var(--spacing-20, 20px)", gap: "var(--spacing-16, 16px)", minHeight: 0 }}>
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} onSubmit={handleFeedbackSubmit} />}
      {docViewer && <SourceDocViewer fileName={docViewer} onClose={() => setDocViewer(null)} />}
      {toastOpen && <FeedbackToast onClose={() => setToastOpen(false)} />}
      {pendingEdit && (
        <ConfirmDialog
          title="Edit this message?"
          body="Editing this message will remove all replies and messages below it. This action cannot be undone."
          confirmLabel="Edit & Remove"
          cancelLabel="Cancel"
          onConfirm={() => applyEdit(pendingEdit.id, pendingEdit.text)}
          onCancel={() => setPendingEdit(null)}
        />
      )}

      {/* Main card */}
      <div style={{ flex: "1 0 0", borderRadius: "var(--border-radius-lg, 8px)", backgroundColor: "rgba(255,255,255,0.4)", border: "1.5px solid rgba(255,255,255,1)", display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", padding: "var(--spacing-20, 20px)", position: "relative", minHeight: 0, minWidth: 0 }}>
        {/* Title row + New Chat */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", width: "100%", gap: "var(--spacing-16, 16px)", flexShrink: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-4, 4px)", minWidth: 0, textAlign: "left" }}>
            <h1 style={{ fontWeight: 400, fontSize: "var(--font-size-28, 1.75rem)", lineHeight: "1.3", letterSpacing: "-0.4px", color: "var(--eyp-color-blue-900-dark-blue, #072e65)", margin: 0, textAlign: "left" }}>DBS AI Chatbot</h1>
            <p style={{ fontWeight: 300, fontSize: "var(--font-size-14, 0.875rem)", lineHeight: "1.5", color: "var(--text-colors-default-primary, #2e2e38)", margin: 0, textAlign: "left" }}>Ask questions on DBS datasets and get AI-generated insights from approved document extracts.</p>
          </div>
          <NewChatButton onClick={onNewChat} />
        </div>

        {/* Scrollable conversation */}
        <div ref={scrollRef} className="eyp-scroll" style={{ flex: "1 0 0", display: "flex", flexDirection: "column", gap: "var(--spacing-20, 20px)", overflowY: "auto", padding: "0 var(--spacing-8, 8px) var(--spacing-4, 4px) var(--spacing-4, 4px)", minHeight: 0, minWidth: 0 }}>
          {turns.map((turn, idx) => (
            <div key={`${turn.id}-${idx}`} style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-8, 8px)" }}>
              {editingId === turn.id ? (
                <UserMessageEditor turn={turn} onSave={(t) => handleEditSave(turn.id, t)} onCancel={() => setEditingId(null)} />
              ) : (
                <UserMessage turn={turn} onEdit={() => !busy && setEditingId(turn.id)} />
              )}
              {turn.loading && <LoadingMessage time={turn.time} />}
              {!turn.loading && turn.streaming && <StreamingMessage turn={turn} />}
              {!turn.loading && !turn.streaming && turn.error && (
                <ErrorMessage turn={turn} onRetry={() => handleRetry(turn.id)} />
              )}
              {!turn.loading && !turn.streaming && !turn.error && turn.variants.length > 0 && (
                <AIResponseMessage
                  turn={turn}
                  onFeedback={(sentiment) => openFeedback(turn.id, turn.activeIndex, sentiment)}
                  onRegenerate={() => handleRegenerate(turn.id)}
                  onPrev={() => handlePaginate(turn.id, -1)}
                  onNext={() => handlePaginate(turn.id, 1)}
                  onViewSource={(file) => setDocViewer(file)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Suggested prompts + Composer (grouped) */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-12, 12px)", width: "100%", flexShrink: 0 }}>
          {/*turns.length === 1 && !busy && turns[0].variants.length > 0 && !turns[0].error && (
            <SuggestedPrompts prompts={SUGGESTED_PROMPTS} onSelect={(p) => handleSend(p)} />
          )*/}
          <Composer value={composer} onChange={setComposer} onSubmit={handleSend} onStop={handleStop} appState={busy ? "loading" : "idle"} mode={mode} onModeChange={setMode} />
        </div>
      </div>

      <Footer />
    </div>
  );
}
